type CredentialVerificationMergeFields = {
  status?: string;
  authMaterialExpiry?: unknown | null;
  artifacts?: unknown;
};

const hasDecryptedArtifactValue = (artifact: unknown): boolean => {
  if (typeof artifact !== 'object' || artifact === null) {
    return false;
  }

  const value = (artifact as { value?: unknown }).value;
  return typeof value === 'string' && value.length > 0;
};

export function mergeCredentialVerificationUpdate<
  TVerification extends CredentialVerificationMergeFields,
>({
  existingArtifacts,
  verification,
  preserveDecryptedArtifactsOnVerified = false,
}: {
  existingArtifacts?: unknown;
  verification: TVerification;
  preserveDecryptedArtifactsOnVerified?: boolean;
}): TVerification {
  const hasExistingArtifacts =
    Array.isArray(existingArtifacts) && existingArtifacts.length > 0;
  const verificationHasDecryptedArtifactValues =
    Array.isArray(verification.artifacts) &&
    verification.artifacts.some(hasDecryptedArtifactValue);

  if (verification.status === 'VERIFIED') {
    if (preserveDecryptedArtifactsOnVerified) {
      if (hasExistingArtifacts) {
        return {
          ...verification,
          artifacts: existingArtifacts,
        } as TVerification;
      }

      if (verificationHasDecryptedArtifactValues) {
        return {
          ...verification,
          artifacts: verification.artifacts,
        } as TVerification;
      }

      return {
        ...verification,
        artifacts: [],
      } as TVerification;
    }

    return {
      ...verification,
      artifacts: verification.artifacts,
    } as TVerification;
  }

  if (verification.status === 'FAILED') {
    return {
      ...verification,
      artifacts: verification.artifacts ?? [],
    } as TVerification;
  }

  if (
    verification.status === 'PENDING' ||
    verification.status === 'VERIFYING'
  ) {
    return {
      ...verification,
      authMaterialExpiry: null,
      artifacts: [],
    } as TVerification;
  }

  return {
    ...verification,
    artifacts: existingArtifacts ?? verification.artifacts,
  } as TVerification;
}

export function getSessionArtifactsReadinessFromSummaries(
  artifacts: Array<{ kind: string }>,
): 'ready' | 'reverify' {
  const hasStorageArtifacts = artifacts.some(
    (artifact) =>
      artifact.kind === 'localStorage' || artifact.kind === 'sessionStorage',
  );

  if (hasStorageArtifacts) {
    return 'ready';
  }

  return 'reverify';
}
